<!-- src/views/DashboardView.vue -->
<template>
  <div class="p-4 md:p-6 pt-2 md:pt-4">
    <div class="max-w-7xl mx-auto space-y-6 animate-fadeIn text-left"> 

      <!-- 1. PREMIUM COMMAND HEADER -->
      <div class="bg-[#0A1630] rounded-[40px] p-6 md:p-8 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden text-left border border-slate-800 no-print">
        <div class="absolute top-0 right-0 p-12 opacity-5 text-left pointer-events-none">
          <Squares2X2Icon class="h-32 w-32 text-white" />
        </div>

        <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8 text-left">
          <div class="text-left">
            <h1 class="text-2xl md:text-3xl font-black text-white uppercase tracking-tight leading-none text-left">
              Command Centre
            </h1>
            <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3 flex items-center gap-2 text-left leading-none">
              <span class="w-1.5 h-1.5 bg-[#20FCF1] rounded-full animate-pulse"></span>
              Protocol Active: {{ userStore.userProfile?.firstName || 'System' }} {{ userStore.userProfile?.lastName || 'Operator' }} 
              <template v-if="resolvedDepartment">
                • {{ resolvedDepartment }}
              </template>
            </p>
          </div>

          <!-- Quick Navigation Cards -->
          <div class="flex flex-col sm:flex-row gap-4 shrink-0 w-full md:w-auto">
            <!-- Ask SIM Card -->
            <div @click="triggerSim" class="bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 transition-all cursor-pointer flex items-start gap-3 w-full sm:w-[220px]">
              <ChatBubbleLeftRightIcon class="h-5 w-5 text-[#20FCF1] shrink-0 mt-0.5" />
              <div class="text-left">
                <p class="font-black uppercase text-[9px] text-white tracking-widest mb-1">Ask SIM</p>
                <p class="text-slate-400 text-[10px] font-medium leading-tight">Get instant workspace help</p>
              </div>
            </div>

            <!-- My Tasks Card -->
            <router-link to="/tasks" class="bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 transition-all flex items-start gap-3 w-full sm:w-[220px]">
              <ClipboardDocumentCheckIcon class="h-5 w-5 text-[#20FCF1] shrink-0 mt-0.5" />
              <div class="text-left">
                <p class="font-black uppercase text-[9px] text-white tracking-widest mb-1">My Tasks</p>
                <p class="text-slate-400 text-[10px] font-medium leading-tight">Manage your active tasks</p>
              </div>
            </router-link>
          </div>
        </div>
      </div>

      <!-- 🛡️ STANDALONE DEDICATED SYSTEM BROADCAST (Direct Symmetrical Media Display) -->
      <transition name="fade-slide">
        <div 
          v-if="notification && notification.enabled && isBroadcastTimeValid && (notification.broadcastTarget === 'both' || notification.broadcastTarget === 'worker' || !notification.broadcastTarget)"
          :class="notification.type === 'alert' ? 'border-[#EF4444]' : 'border-[#20FCF1]'"
          class="bg-white border-2 rounded-[40px] p-8 md:p-10 shadow-2xl relative overflow-hidden text-left border-solid mb-12 flex flex-col md:flex-row justify-between items-start gap-8"
        >
          <!-- Subtly shaded safety accent layers -->
          <div v-if="notification.type === 'alert'" class="absolute inset-0 bg-[#EF4444]/5 animate-pulse pointer-events-none"></div>
          <div v-else class="absolute inset-0 bg-[#20FCF1]/5 opacity-30 pointer-events-none"></div>
          
          <div class="relative z-10 flex items-start gap-6 text-left flex-grow min-w-0">
            <!-- Icon styled dynamically to match the selected priority color -->
            <div class="shrink-0 mt-1 bg-slate-50 p-3 rounded-2xl border border-slate-100 shadow-sm">
              <MegaphoneIcon v-if="notification.type === 'info'" class="h-6 w-6 text-[#20FCF1]" />
              <ExclamationTriangleIcon v-else class="h-6 w-6 text-[#EF4444] animate-bounce" />
            </div>
            
            <div class="text-left space-y-4 flex-grow min-w-0">
              <!-- Subtitle styled dynamically to match the selected priority color -->
              <span 
                class="text-[9px] font-black uppercase tracking-[0.3em] block text-left leading-none" 
                :class="notification.type === 'alert' ? 'text-[#EF4444]' : 'text-[#20FCF1]'"
              >
                {{ notification.type === 'alert' ? 'Critical System Alert' : 'Operational Update' }}
              </span>

              <!-- Dynamic Broadcast Title (Matches Admin Dashboard layout) -->
              <h2 v-if="notification.broadcastTitle" class="text-xl sm:text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none text-left">
                {{ notification.broadcastTitle }}
              </h2>

              <!-- Safe standard text rendering -->
              <p class="text-slate-600 text-sm font-medium leading-relaxed text-left whitespace-pre-wrap">
                {{ notification.message }}
              </p>
            </div>
          </div>

          <!-- Deepened Video Display aligned to top right with Autoplay -->
          <div v-if="notification.broadcastMediaUrl" class="shrink-0 w-full md:w-[320px] lg:w-[400px] h-[220px] lg:h-[275px] no-print relative z-10 self-start rounded-[24px] overflow-hidden bg-transparent flex items-center justify-center">
            <!-- Dynamic Image Rendering -->
            <template v-if="notification.broadcastMediaType === 'image'">
              <a :href="notification.broadcastMediaUrl" target="_blank" class="flex items-center justify-center w-full h-full group hover:scale-[1.02] transition-transform duration-300">
                <img 
                  :src="resolvedBroadcastMediaUrl" 
                  class="max-w-full max-h-full object-contain mx-auto" 
                  @error="(e) => e.target.src = 'https://images.unsplash.com/photo-1532102235608-dc8fc689c2af?auto=format&fit=crop&w=1200&q=80'"
                  alt="Broadcast Media" 
                />
              </a>
            </template>
            
            <!-- Dynamic Video Rendering -->
            <template v-else-if="notification.broadcastMediaType === 'video'">
              <iframe 
                v-if="resolvedBroadcastMediaUrl"
                class="w-full h-full bg-slate-900 rounded-[24px] border border-slate-100 shadow-md" 
                :src="resolvedBroadcastMediaUrl" 
                frameborder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowfullscreen
              ></iframe>
            </template>
          </div>
        </div>
      </transition>

      <!-- WELCOME & SIM ASSIST HUB -->
      <div v-if="userStore.isAuthReady" class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6 no-print">
        <!-- Welcome Card -->
        <div class="bg-white p-8 rounded-[32px] border border-slate-200/60 shadow-sm flex items-start gap-6 text-left relative overflow-hidden group hover:border-[#20FCF1]/30 transition-all duration-300">
          <div class="flex shrink-0">
            <img v-if="userStore.userProfile?.photoUrl" :src="userStore.userProfile?.photoUrl" class="w-12 h-12 rounded-2xl object-cover shadow-sm border border-slate-200" alt="Profile" />
            <div v-else class="w-12 h-12 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center text-[#0A1630] shadow-inner">
               <UserPlusIcon class="h-6 w-6 text-[#0A1630]" />
            </div>
          </div>
          <div class="space-y-4">
            <h3 class="text-base font-black text-[#0A1630] uppercase tracking-wider flex items-center gap-2">
              Welcome, {{ userStore.userProfile?.firstName || 'Operator' }}
            </h3>
            <p class="text-xs text-slate-500 font-medium leading-relaxed">
              Welcome to your Command Centre. Use the modules below to access the tools and resources you need to stay safe, compliant and productive.
            </p>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 text-[10px] font-black text-slate-400 uppercase tracking-wider">
               <span class="flex items-center gap-1.5"><span class="text-[#20FCF1]">✓</span> Clock In</span>
               <span class="flex items-center gap-1.5"><span class="text-[#20FCF1]">✓</span> Safety Forms</span>
               <span class="flex items-center gap-1.5"><span class="text-[#20FCF1]">✓</span> Training Index</span>
            </div>
          </div>
        </div>

        <!-- SIM Assist Card -->
        <div class="bg-white p-8 rounded-[32px] border border-slate-200/60 shadow-sm flex items-start gap-6 text-left relative overflow-hidden group hover:border-[#20FCF1]/30 transition-all duration-300">
          <div class="flex shrink-0 w-12 h-12 items-center justify-center">
             <img src="/images/sim-head.png" class="w-12 h-12 object-contain" alt="SIM" />
          </div>
          <div class="space-y-4 w-full">
            <h3 class="text-base font-black text-[#0A1630] uppercase tracking-wider">
              SIM Assist
            </h3>
            <p class="text-xs text-slate-500 font-medium leading-relaxed">
              SIM can help you find information, complete tasks and navigate the platform with ease.
            </p>
            <div class="flex items-center gap-4 pt-1">
               <button @click="triggerSim" class="px-5 py-2.5 bg-[#0A1630] text-white hover:bg-slate-800 transition-colors rounded-xl text-[10px] font-black uppercase tracking-widest border-none shadow-sm">
                  Ask SIM
               </button>
               <span class="text-[10px] font-black text-[#FD5E00] uppercase tracking-widest">
                  Learn more
               </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 2. AUTH SYNCHRONISATION LOADING STATE -->
      <div v-if="!userStore.isAuthReady" class="text-center py-32 bg-white rounded-[40px] border border-slate-100 shadow-inner text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-4 border-[#0A1630] border-t-[#20FCF1] mx-auto mb-6 text-center"></div>
        <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Initialising Operational Registry...</p>
      </div>

      <!-- 3. MAIN OPERATIONAL GRID -->
      <div v-else class="dashboard-content animate-fadeIn text-left pb-4"> 
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-left">
          <div 
            v-for="card in dashboardCards" 
            :key="card.title" 
            class="group bg-white p-6 rounded-3xl border border-slate-200/60 shadow-sm hover:shadow-md hover:border-[#20FCF1]/20 transition-all duration-300 flex flex-col justify-between text-left min-h-[220px]"
          >
            <!-- Card Content -->
            <div class="space-y-3 text-left">
              <!-- Header with smaller icon on left -->
              <div class="flex items-center gap-4 text-left">
                <!-- Compact Shield Icon with Glow -->
                <div class="relative shrink-0 w-12 h-12 flex items-center justify-center text-center">
                  <!-- Glow Effect -->
                  <div 
                    class="absolute inset-0 rounded-full blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500" 
                    :style="{ backgroundColor: card.color }"
                  ></div>
                  <!-- The Shield SVG -->
                  <svg class="absolute inset-0 w-full h-full drop-shadow-sm" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" :fill="card.color" fill-opacity="0.1" :stroke="card.color" stroke-width="1.5"/>
                  </svg>
                  <component :is="card.icon" class="w-5 h-5" :style="{ color: card.color }" />
                </div>

                <h2 class="text-sm font-black text-[#0A1630] uppercase tracking-wider mb-0 text-left">
                  {{ card.title }}
                </h2>
              </div>

              <!-- Brief Description -->
              <p class="text-xs text-slate-500 font-medium leading-relaxed text-left min-h-[36px]">
                {{ card.description }}
              </p>
            </div>

            <!-- Action Tier -->
            <div class="space-y-3 mt-3 pt-2.5 border-t border-slate-100 text-left">
              <!-- Single primary button -->
              <router-link :to="card.primaryAction.to" class="btn-command-primary-new">
                {{ card.primaryAction.text }}
              </router-link>
              
              <!-- Text Links Underneath -->
              <div class="flex items-center justify-center gap-3 text-[10px] font-black uppercase tracking-wider">
                <template v-for="(link, lIdx) in card.links" :key="lIdx">
                  <router-link :to="link.to" class="text-[#0A1630] hover:text-[#20FCF1] transition-colors">
                    {{ link.text }}
                  </router-link>
                  <span v-if="lIdx < card.links.length - 1" class="text-slate-200">|</span>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>

  <!-- 🛡| COMMAND POP-UP (MODAL) -->
  <transition name="scale-in">
    <div v-if="showPopup" class="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 md:p-8 lg:p-12 text-left">
      <div @click="closePopup" class="absolute inset-0 bg-[#0A1630]/95 backdrop-blur-xl text-left"></div>
      
      <!-- Adaptive outline matching chosen popup theme -->
      <div 
        :class="notification.popupType === 'alert' ? 'border-[#EF4444]' : 'border-[#20FCF1]'"
        class="relative bg-white w-full max-w-7xl max-h-[90vh] rounded-[40px] shadow-[0_50px_100px_rgba(0,0,0,0.8)] overflow-hidden animate-scaleIn text-left border-2 flex flex-col lg:flex-row"
      >
        <div class="grid grid-cols-1 lg:grid-cols-5 w-full overflow-y-auto lg:overflow-hidden text-left">
          <div class="bg-slate-900 lg:col-span-3 aspect-video lg:aspect-auto flex items-center justify-center relative text-left overflow-hidden min-h-[300px] sm:min-h-[450px]">
            <template v-if="notification.popupMediaType === 'video' || isDriveLink">
              <iframe 
                v-if="resolvedPopupMediaUrl"
                class="w-full h-full relative z-20" 
                :src="resolvedPopupMediaUrl" 
                frameborder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowfullscreen
              ></iframe>
              <div v-else class="flex flex-col items-center gap-4 text-center z-20">
                  <ShieldCheckIcon class="h-12 w-12 text-[#20FCF1] opacity-20" />
                  <p class="text-[10px] font-black text-white/40 uppercase tracking-widest">Protocol Media Awaiting Link</p>
              </div>
            </template>

            <template v-else>
              <img 
                :src="resolvedPopupMediaUrl || 'https://images.unsplash.com/photo-1532102235608-dc8fc689c2af?auto=format&fit=crop&w=1200&q=80'" 
                class="w-full h-full object-cover relative z-20 transition-opacity duration-1000" 
                @error="(e) => e.target.src = 'https://images.unsplash.com/photo-1532102235608-dc8fc689c2af?auto=format&fit=crop&w=1200&q=80'"
                alt="Announcement"
              >
              <div class="absolute inset-0 bg-gradient-to-tr from-[#20FCF1]/10 to-transparent z-10"></div>
            </template>
            
            <!-- Authority Badge matching popup priority style -->
            <div 
              :class="notification.popupType === 'alert' ? 'bg-[#EF4444]' : 'bg-[#20FCF1]'"
              class="absolute top-4 sm:top-8 left-4 sm:left-8 text-[#0A1630] px-4 py-2 rounded-xl font-black uppercase text-[9px] tracking-[0.3em] shadow-2xl z-30 flex items-center gap-2"
            >
              <span class="w-1.5 h-1.5 bg-[#0A1630] rounded-full animate-pulse"></span> Transmission Active
            </div>
          </div>

          <div class="lg:col-span-2 p-8 sm:p-10 md:p-16 flex flex-col text-left bg-white relative z-20 overflow-y-auto">
            <div class="flex-grow text-left">
              <!-- Subtitle matching popup priority style -->
              <span 
                class="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.4em] block mb-4 sm:mb-6 text-left"
                :class="notification.popupType === 'alert' ? 'text-[#EF4444]' : 'text-[#20FCF1]'"
              >
                System Command Announcement
              </span>
              <h2 class="text-2xl sm:text-3xl md:text-4xl font-black text-[#0A1630] uppercase tracking-tight leading-none mb-6 sm:mb-10 text-left">
                {{ notification.popupTitle }}
              </h2>
              <p class="whitespace-pre-wrap text-sm sm:text-base text-left text-slate-500 font-medium leading-relaxed max-h-[450px] overflow-y-auto custom-scrollbar pr-2 sm:pr-6">
                {{ notification.popupMessage }}
              </p>
            </div>

            <!-- Button matching popup priority style -->
            <div class="mt-8 sm:mt-12 pt-8 sm:pt-10 border-t border-slate-100 text-center">
               <button 
                @click="closePopup" 
                class="w-full py-5 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-xs tracking-[0.3em] transition-all shadow-xl text-center flex items-center justify-center gap-4 group cursor-pointer"
                :class="notification.popupType === 'alert' ? 'hover:bg-[#EF4444] hover:text-white' : 'hover:bg-[#20FCF1] hover:text-[#0A1630]'"
               >
                 <span>Acknowledge Transmission</span>
                 <ShieldCheckIcon class="h-4 w-4 sm:h-5 sm:w-5 text-[#20FCF1] group-hover:text-current" />
               </button>
            </div>
          </div>
        </div>

        <button @click="closePopup" class="absolute top-4 sm:top-8 right-4 sm:right-8 text-[#0A1630]/30 hover:text-[#EF4444] transition-colors z-30 cursor-pointer">
          <XMarkIcon class="h-8 w-8 sm:h-10 sm:w-10" />
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, onUnmounted, watch } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { db, auth } from '@/firebaseConfig';
import { doc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { 
  UserPlusIcon, 
  ShieldCheckIcon, 
  AcademicCapIcon, 
  WrenchScrewdriverIcon, 
  TruckIcon, 
  InformationCircleIcon,
  ClockIcon,
  Squares2X2Icon,
  MegaphoneIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();

// SYSTEM BROADCAST & POPUP STATE
const notification = ref(null);
const showPopup = ref(false);
let unsubscribeNotif = null;

const completedFormsCount = ref(0);

const triggerSim = () => {
  const simBtn = document.querySelector('.sim-trigger-btn') || document.querySelector('#sim-button');
  if (simBtn) {
    simBtn.click();
  } else {
    console.log("SIM handshaking requested.");
  }
};

/**
 * 🛡️ MISSION CRITICAL: DRIVE PROTOCOL
 */
const isDriveLink = computed(() => {
  return notification.value?.popupMediaUrl?.includes('drive.google.com');
});

/**
 * 🛡️ RESOLVE BROADCAST MEDIA
 * Dynamically converts standard video, YouTube, or Google Drive share links into embeddable / renderable assets
 */
const resolvedBroadcastMediaUrl = computed(() => {
  if (!notification.value || !notification.value.broadcastMediaUrl) return '';
  const url = notification.value.broadcastMediaUrl.trim();

  // If it's a Google Drive link and we are displaying an image, convert to direct image stream
  if (url.includes('drive.google.com')) {
    const fileIdMatch = url.match(/(?:\/d\/|id=)([a-zA-Z0-9_-]+)/);
    if (fileIdMatch && fileIdMatch[1]) {
      const fileId = fileIdMatch[1];
      if (notification.value.broadcastMediaType === 'image') {
        return `https://lh3.googleusercontent.com/d/${fileId}`;
      }
      return `https://drive.google.com/file/d/${fileId}/preview?autoplay=1`;
    }
  }

  // YouTube conversions (for videos)
  if (url.includes('watch?v=')) {
    const videoIdMatch = url.match(/(?:v=)([^&]+)/);
    const id = videoIdMatch ? videoIdMatch[1] : '';
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&enablejsapi=1`;
  }
  if (url.includes('youtu.be/')) {
    const parts = url.split('/');
    const id = parts[parts.length - 1];
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&enablejsapi=1`;
  }
  return url;
});

/**
 * 🛡️ RESOLVE POP-UP MEDIA
 */
const resolvedPopupMediaUrl = computed(() => {
  if (!notification.value || !notification.value.popupMediaUrl) return '';
  const url = notification.value.popupMediaUrl.trim();

  // Google Drive conversion
  if (url.includes('drive.google.com')) {
    const fileIdMatch = url.match(/(?:\/d\/|id=)([a-zA-Z0-9_-]+)/);
    if (fileIdMatch && fileIdMatch[1]) {
      const fileId = fileIdMatch[1];
      if (notification.value.popupMediaType === 'image') {
        return `https://lh3.googleusercontent.com/d/${fileId}`;
      }
      return `https://drive.google.com/file/d/${fileId}/preview?autoplay=1`;
    }
  }

  // YouTube conversions
  if (url.includes('watch?v=')) {
    const videoIdMatch = url.match(/(?:v=)([^&]+)/);
    const id = videoIdMatch ? videoIdMatch[1] : '';
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&enablejsapi=1`;
  }
  if (url.includes('youtu.be/')) {
    const parts = url.split('/');
    const id = parts[parts.length - 1];
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&enablejsapi=1`;
  }
  return url;
});

// Broadcast date validation gating
const isBroadcastTimeValid = computed(() => {
  const n = notification.value;
  if (!n) return false;
  const now = new Date();
  
  // Safely parse start/end date only if they are valid non-empty strings
  const start = (n.broadcastStart && n.broadcastStart !== '') ? new Date(n.broadcastStart) : null;
  const end = (n.broadcastEnd && n.broadcastEnd !== '') ? new Date(n.broadcastEnd) : null;
  
  const isStartValid = start && !isNaN(start.getTime());
  const isEndValid = end && !isNaN(end.getTime());
  
  return (!isStartValid || now >= start) && (!isEndValid || now <= end);
});

/**
 * 🛡️ DEPARTMENT RESOLUTION HANDSHAKE
 */
const resolvedDepartment = computed(() => {
    const profile = userStore.userProfile;
    if (!profile) return '';
    const dept = profile.department || (Array.isArray(profile.departments) && profile.departments[0]) || '';
    return String(dept).replace(/_/g, ' ').toUpperCase();
});

/**
 * 🛡️ EVALUATE POP-UP VISIBILITY
 */
const evaluatePopupVisibility = () => {
  const n = notification.value;
  if (!n || !n.popupEnabled) {
      showPopup.value = false;
      return;
  }

  const popupTarget = n.popupTarget || 'both';
  const isTargetMatch = popupTarget === 'both' || popupTarget === 'worker';
  if (!isTargetMatch) {
      showPopup.value = false;
      return;
  }

  const now = new Date();
  const start = (n.popupStart && n.popupStart !== '') ? new Date(n.popupStart) : null;
  const end = (n.popupEnd && n.popupEnd !== '') ? new Date(n.popupEnd) : null;
  
  const isStartValid = start && !isNaN(start.getTime());
  const isEndValid = end && !isNaN(end.getTime());
  const isTimeValid = (!isStartValid || now >= start) && (!isEndValid || now <= end);
  
  const sessionKey = `seen_popup_${n.popupId}`;
  const alreadySeen = sessionStorage.getItem(sessionKey);

  if (isTimeValid && !alreadySeen) {
      showPopup.value = true;
  } else {
      showPopup.value = false;
  }
};

const closePopup = () => {
  if (notification.value?.popupId) {
    sessionStorage.setItem(`seen_popup_${notification.value.popupId}`, 'true');
  }
  showPopup.value = false;
};

const startNotificationListener = () => {
  const cid = userStore.clientId || userStore.userProfile?.clientId;
  if (!cid) return;
  if (unsubscribeNotif) unsubscribeNotif();
  unsubscribeNotif = onSnapshot(doc(db, 'globalNotifications', cid), (snap) => {
    if (snap.exists()) {
      notification.value = snap.data();
      evaluatePopupVisibility();
    } else {
      notification.value = null;
    }
  }, (error) => {
    console.error("Signal Lost:", error);
  });
};

const totalRequiredForms = computed(() => {
  const profile = userStore.userProfile;
  if (!profile) return 10;
  const classification = String(profile.employmentClassification || profile.employmentType || '').toLowerCase().trim();
  if (classification.includes('sub') && classification.includes('contract')) return 1;
  if (classification.includes('self') || classification === 'cis') return 9;
  return 10;
});

const fetchOnboardingProgress = async () => {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  try {
    const q = query(
      collection(db, 'onboardingFormSubmissions'),
      where('userId', '==', uid)
    );
    const snap = await getDocs(q);
    const distinctForms = new Set(snap.docs.map(doc => doc.data().formIdentifier).filter(Boolean));
    completedFormsCount.value = distinctForms.size;
  } catch (e) {
    console.warn("Sentinel Safe - Failed to resolve onboarding progress dynamically:", e);
  }
};

const isOnboardingComplete = computed(() => {
  if (userStore.userProfile?.onboardingFormsCompleted) return true;
  return completedFormsCount.value >= totalRequiredForms.value;
});

watch(() => userStore.isAuthReady, (ready) => {
  if (ready) {
    startNotificationListener();
    fetchOnboardingProgress();
  }
}, { immediate: true });

watch(() => userStore.userProfile?.clientId, (newCid) => {
  if (newCid) {
    startNotificationListener();
    fetchOnboardingProgress();
  }
});

onUnmounted(() => {
  if (unsubscribeNotif) unsubscribeNotif();
});

/**
 * 🛡️ DASHBOARD COMMAND REGISTRY
 */
const dashboardCards = computed(() => {
  const profile = userStore.userProfile;
  if (!profile) return [];

  const isUserActive = String(profile.status || '').trim().toLowerCase() === 'active';
  const showTimeTracker = isOnboardingComplete.value && isUserActive;
  
  const assignedReg = profile.assignedVehicleReg;
  const cards = [];

  if (!showTimeTracker) {
    cards.push({
      title: 'Onboarding',
      icon: UserPlusIcon,
      color: '#20FCF1',
      description: 'Review compliance documents and finalize your onboarding steps.',
      primaryAction: { 
        text: 'Open module', 
        to: '/onboarding-forms' 
      },
      links: [
        { text: 'Onboarding Forms', to: '/onboarding-forms' },
        { text: 'Material', to: '/onboarding-material' }
      ]
    });
  } else {
    cards.push({
      title: 'Time Tracker',
      icon: ClockIcon,
      color: '#20FCF1',
      description: 'Clock in and out, manage your hours and view timesheets.',
      primaryAction: { 
        text: 'Open module', 
        to: '/timesheets/new' 
      },
      links: [
        { text: 'Weekly history', to: '/timesheets/history' },
        { text: 'Timesheets', to: '/timesheets/new' }
      ]
    });
  }

  cards.push({
    title: 'Health & Safety',
    icon: ShieldCheckIcon,
    color: '#22C55E',
    description: 'Access safety forms, submit reports and review procedures.',
    primaryAction: { 
      text: 'Open module', 
      to: { name: 'HealthSafetyFormsList' } 
    },
    links: [
      { text: 'Safety forms', to: { name: 'HealthSafetyFormsList' } },
      { text: 'Submissions', to: '/health-safety/history' }
    ]
  });

  cards.push({
    title: 'Projects',
    icon: WrenchScrewdriverIcon,
    color: '#6366F1',
    description: 'Access the Client Feedback Questionairre and browse active engineering projects',
    primaryAction: { 
      text: 'Open module', 
      to: '/engineering/projects' 
    },
    links: [
      { text: 'Projects', to: '/engineering/projects' },
      { text: 'Forms', to: '/engineering/forms' }
    ]
  });

  cards.push({
    title: 'Training',
    icon: AcademicCapIcon,
    color: '#F97316',
    description: 'View training materials, course index and submit requests.',
    primaryAction: { 
      text: 'Open module', 
      to: { name: 'TrainingMaterial' } 
    },
    links: [
      { text: 'Training index', to: { name: 'TrainingMaterial' } },
      { text: 'Requests', to: '/training-request' }
    ]
  });

  cards.push({
    title: assignedReg ? assignedReg.toUpperCase() : 'Vehicle',
    icon: TruckIcon,
    color: '#EAB308',
    description: 'Complete vehicle checks and access vehicle records.',
    primaryAction: { 
      text: 'Open module', 
      to: '/vehicle-check' 
    },
    links: [
      { text: 'Vehicle checks', to: '/vehicle-check' },
      { text: 'Fleet details', to: '/vehicle-details' }
    ]
  });

  cards.push({
    title: 'Information',
    icon: InformationCircleIcon,
    color: '#3B82F6',
    description: 'Read published documents, guidance and resources.',
    primaryAction: { 
      text: 'Open module', 
      to: { name: 'PublishedDocuments' } 
    },
    links: [
      { text: 'Published docs', to: { name: 'PublishedDocuments' } },
      { text: 'Resources', to: '/useful-links' }
    ]
  });

  return cards;
});
</script>

<style scoped>
.btn-command-primary-new {
  @apply w-full py-3 px-4 bg-[#0A1630] text-white hover:bg-slate-800 rounded-xl transition-all font-black uppercase tracking-widest text-[9px] shadow-sm flex items-center justify-center;
}
.animate-fadeIn { 
  animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
}
@keyframes fadeIn { 
  from { opacity: 0; transform: translateY(20px); } 
  to { opacity: 1; transform: translateY(0); } 
}
.fade-slide-enter-active, .fade-slide-leave-active { 
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1); 
}
.fade-slide-enter-from, .fade-slide-leave-to { 
  opacity: 0; transform: translateX(20px); 
}
.scale-in-enter-active, .scale-in-leave-active { 
  transition: opacity 0.5s ease; 
}
.scale-in-enter-from, .scale-in-leave-to { 
  opacity: 0; 
}
.animate-scaleIn { 
  animation: scaleIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
}
@keyframes scaleIn { 
  from { transform: scale(0.95) translateY(30px); opacity: 0; } 
  to { transform: scale(1) translateY(0); opacity: 1; } 
}
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { 
  background: #20FCF1; border-radius: 10px; 
}
</style>